import _ from 'lodash'
import Vue from 'vue'
import { Module, Action, getModule, Mutation, VuexModule } from 'vuex-module-decorators'
import { TCWUnexpectedError, TCWBadDataStructureError } from '@/plugins/TCWBaseError'
import { MethodType, ApiAgent } from '@/plugins/ApiRequest'
import store from '@/store'
import { IFormStore, UpdatePayload } from '@/store/types'
import { sessionStore } from '@/store/session/SessionStoreModule'
import * as ApiPath from '@/tcw/constant/ApiPath'
import { ApiGeneralSinglePayload } from '@/tcw/api/types'
import * as tcw from '@/tcw/utils/TcwService'
import { KeyInfo } from '@/components/common/form/types'
import Getsudo from '@/tcw/domain/Getsudo'

@Module({
  name: 'TeishutsuInputFormStore',
  store,
  namespaced: true,
  dynamic: true
})
export class TeishutsuInputFormStore extends VuexModule
  implements IFormStore<ApiGeneralSinglePayload> {
  // 編集データ
  protected editTargetData: ApiGeneralSinglePayload | null = null
  // 編集前データ保持用。新規データの場合はnull
  protected origin: ApiGeneralSinglePayload | null = null

  /****************
   * Getter
   ****************/
  public get ready(): boolean {
    return this.editTargetData !== null
  }
  get isNewRecord(): boolean {
    return this.editTargetData?.id === null
  }
  public get editData(): ApiGeneralSinglePayload {
    return this.editTargetData ? this.editTargetData : {}
  }
  public get isLock(): boolean {
    return false
  }
  public get apiPath(): { [type: string]: string } {
    return ApiPath.Paths.TeishutsuInput
  }
  public get isChanged(): boolean {
    if (this.editTargetData === null) {
      return this.origin !== null
    }
    if (this.origin === null) {
      return this.editTargetData !== null
    }
    const destKeys = Object.keys(this.editTargetData)
    const srcKeys = Object.keys(this.origin)

    if (destKeys.length !== srcKeys.length) {
      return true
    }

    const isChanged = destKeys.some(d => {
      return this.editTargetData && this.origin ? this.editTargetData[d] !== this.origin[d] : false
    })

    return isChanged
  }

  public get targetGetsudo(): Getsudo | null {
    if (this.editTargetData === null) {
      return null
    }
    if (this.editTargetData.teishutsuGetsudo instanceof Getsudo) {
      return this.editTargetData.teishutsuGetsudo
    }
    if (typeof this.editTargetData.teishutsuGetsudo !== 'string') {
      return null
    }

    const teishutsuGetsudo = this.editTargetData.teishutsuGetsudo

    // フォーマット判定
    let year, month
    if (teishutsuGetsudo.indexOf('-') > 0) {
      // ハイフンつなぎ
      ;[year, month] = teishutsuGetsudo.split('-')
    } else if (teishutsuGetsudo.indexOf('/') > 0) {
      // スラッシュつなぎ
      ;[year, month] = teishutsuGetsudo.split('/')
    }

    if (year && month) {
      return Getsudo.of(year, month)
    } else {
      return null
    }
  }

  /****************
   * Action
   ****************/

  /**
   * 空データ取得
   */
  @Action({ rawError: true })
  public async create(): Promise<void> {
    await this.fetchEmpty()
  }

  /**
   * 空データ取得
   */
  @Action({ rawError: true })
  public async fetchEmpty(): Promise<ApiGeneralSinglePayload> {
    // API実行
    try {
      const apiAgent = ApiAgent.create(tcw.getApiUrlBase(), sessionStore.sessionToken)
      const response: ApiGeneralSinglePayload = await apiAgent.call<ApiGeneralSinglePayload>(
        this.apiPath.empty,
        'GET'
      )

      // STOREにセットする
      this.setEditTarget(response)

      // 呼び出し側に送る
      return response
    } catch (e) {
      console.error('failure fetch form data', e)
      return Promise.reject(e)
    }
  }

  /**
   * 編集データセット
   * @param id 編集データID
   */
  @Action({ rawError: true })
  public async fetch(keyInfo: KeyInfo): Promise<ApiGeneralSinglePayload> {
    // API実行
    const path = ApiPath.buildPath(this.apiPath.get, keyInfo)
    const apiAgent = ApiAgent.create(tcw.getApiUrlBase(), sessionStore.sessionToken)

    console.log('API呼び出し')
    const response: ApiGeneralSinglePayload = await apiAgent.call<ApiGeneralSinglePayload>(
      path,
      'GET'
    )

    // STOREにセットする
    console.log('STOREにセットする')
    this.setEditTarget(response)

    // 呼び出し側に送る
    return response
  }

  /**
   * 編集データサーバ送信
   */
  @Action({ rawError: true })
  public async submit(): Promise<ApiGeneralSinglePayload> {
    console.log('start submit')
    if (this.ready && this.targetGetsudo) {
      // APIパラメータを生成
      const pathBase: string = this.isNewRecord ? this.apiPath.post : this.apiPath.put
      const path = ApiPath.buildPath(pathBase, this.targetGetsudo.yearMonth())
      const method: MethodType = this.isNewRecord ? 'POST' : 'PUT'

      // API実行
      const apiAgent = ApiAgent.create(tcw.getApiUrlBase(), sessionStore.sessionToken)
      const response: ApiGeneralSinglePayload = await apiAgent.call<ApiGeneralSinglePayload>(
        path,
        method,
        this.editData as ApiGeneralSinglePayload
      )

      return response
    } else {
      // データがない場合はエラー
      return Promise.reject(new TCWUnexpectedError('データがセットされていません'))
    }
  }

  /**
   * 編集データサーバ送信
   */
  @Action({ rawError: true })
  public async delete(key: KeyInfo): Promise<boolean> {
    // API実行
    const path = ApiPath.buildPath(this.apiPath.delete, key)
    const apiAgent = ApiAgent.create(tcw.getApiUrlBase(), sessionStore.sessionToken)
    await apiAgent.call(path, 'DELETE')
    return Promise.resolve(true)
  }

  @Action({ rawError: true })
  public updateRecord(payload: UpdatePayload): void {
    this.update(payload)
  }
  @Action({ rawError: true })
  public clear(): void {
    this.setEditTarget(null)
  }

  /****************
   * Mutation
   ****************/
  @Mutation
  public update(payload: UpdatePayload): void {
    if (this.editTargetData === null) {
      throw new TCWBadDataStructureError('データがロードされていません')
    }
    Object.keys(payload).forEach((property: string) => {
      const updateValue = payload[property]
      console.debug(`update record: ${property} => ${updateValue}`)
      if (this.editTargetData != null) {
        // non-null-assertion回避
        Vue.set(this.editTargetData, property, updateValue)
      }
    })
  }
  @Mutation
  protected setEditTarget(record: ApiGeneralSinglePayload | null): void {
    this.editTargetData = record
    this.origin = _.cloneDeep(record)
  }
}

export const teishutsuInputStore = getModule(TeishutsuInputFormStore)
